import { useRef, useState } from 'react';
import { RespondentPicker } from '../components/RespondentPicker';
import type { DragEvent } from 'react';
import { PersonIcon, PhoneIcon, UploadIcon } from '../components/Icons';
import { useAlert } from '../components/Alert';
import { submitComplaint } from '../api/citizen';
import { errText, isInconclusive } from '../api/client';
import { WRITE_BUSY_HINT, WRITE_BUSY_TITLE, WRITE_INCONCLUSIVE_TITLE } from '../api/write-timeout';
import type { CitizenSubmitResult } from '../api/contract';
import {
  BLANK_ORIGINS,
  COMPLAINT_ROLES,
  EMPTY_ELEMENTS,
  EVIDENCE_KIND_LABEL,
  MAX_FILES,
  MAX_NAME_LEN,
  MAX_ROLE_OTHER_LEN,
  ROLE_OTHER,
  acceptEvidence,
  applyPrefill,
  attachmentLimitHintText,
  buildSubmitBody,
  checkComplaint,
  evidenceAcceptAttr,
  fmtSize,
  usedRawBytes,
} from '../util/complaint-rules';
import type {
  CaseElements,
  ComplaintDraft,
  ElementOrigins,
  PickedEvidence,
  RespondentOrgPick,
  RespondentPersonPick,
} from '../util/complaint-rules';
import { usePrefill } from '../hooks/usePrefill';
import { useAreas } from '../hooks/useAreas';
import { useComplaintExample } from '../hooks/useComplaintExample';
import type { CaseElementsPanel } from '../components/CaseElements';
import PcConfirm from './PcConfirm';

/**
 * PC 端投诉表单。五块内容与手机端一一对应，字段与校验**同一份代码**
 * （`src/util/complaint-rules.ts`）—— 不在这里另写一套 if。
 *
 * ★ 2026-09-03 协会令三处同步（完整来由见 `pages/Submit.tsx` 文件头）：
 *   ⑥「投诉人员请填／投诉企业请填」（在共用组件 RespondentPicker 里，本文件不动）；
 *   ⑨「事情经过」→「投诉内容及诉求」；
 *   ⑩ 身份选「其他」后展开补充输入框（与手机端同一套上限、占位、清空与提交形状）。
 *
 * PC 与手机的差别只在**交互形态**，不在规则：
 *   · 上传：手机是「点开相册」，PC 加了**拖拽**（PC 用户的预期），两条路进同一个
 *     `acceptEvidence()`，判断完全一致；
 *   · 联系方式：PC 宽度够，姓名与电话并排；窄下来自动落成一列。
 *
 * ★ 所有失败提示走弹窗（`useAlert`），页面里不塞红字。
 *   根因见 `components/Alert.tsx`：表单一长，挂在顶部的报错就落在可视区外，
 *   用户在底部点提交，现场表现是「点了没反应」。
 * ★ 不写任何解释系统怎么运转的文字（不写「正在调用接口」「AI 识别中」「通道未接通」）。
 *   市民只需要知道成功了还是失败了、失败要怎么办。
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ 2026-08-26 主公令：提交前加一屏**信息确认**，
 *     流程改成「填表 → 提交 → 确认 → 落库」，与手机端同步。
 *
 *   草稿（正文 ＋ 已选证据的 base64）**全程留在本组件的 state 里**：
 *   确认屏 `PcConfirm` 是个纯展示件，只拿走一份只读的 draft。
 *   所以「返回修改」＝把画法换回表单，state 一个字节都没动过，一个字都不会丢。
 *   —— 这是这一步最容易做砸的地方：市民写了几百字，回去发现全没了，
 *      比不加这一屏还糟。**不搬家就没有搬丢的可能。**
 *
 *   PC 页没有路由（见 `pc/main.tsx` 文件头），所以刷新＝整页重来，草稿本来就会清空；
 *   而确认屏只是本组件的一个 state，**刷新后一定落回空白表单，
 *   不存在一个能点「确认」的空白确认页**。
 * ────────────────────────────────────────────────────────────────────────────
 */

export default function PcForm({ onDone }: { onDone: (r: CitizenSubmitResult) => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');
  /** 身份选「其他」时的补充说明（2026-09-03 协会令）。没选「其他」时恒为空串。 */
  const [roleOther, setRoleOther] = useState('');
  const [targetPhone, setTargetPhone] = useState('');
  /* 被投诉方是**两条独立的线**（2026-08-27）：各自一份 state，谁也不影响谁。
     合成一个 state 就会重蹈「选了人顺手把机构也填了」的覆辙。 */
  const [person, setPerson] = useState<RespondentPersonPick | null>(null);
  const [org, setOrg] = useState<RespondentOrgPick | null>(null);
  const [text, setText] = useState('');
  /* 示范文字后台可配（2026-09-23），与手机端同一个 hook、同一条接口。 */
  const example = useComplaintExample();
  const [files, setFiles] = useState<PickedEvidence[]>([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  /** 当前画哪一屏。草稿不随它搬家，所以来回切多少次都不丢。 */
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const fileRef = useRef<HTMLInputElement | null>(null);
  /* dragenter/dragleave 在子元素之间移动时会成对乱飞，用计数器才不会闪 */
  const dragDepth = useRef(0);
  /**
   * 防重复提交的**真闸门**。
   * 只靠 `busy` 这个 state 不够：两次点击是两个独立事件，第二次的处理函数完全可能在
   * 第一次的 setState 落地前就跑起来，读到的还是旧的 `busy=false` —— 于是落两条记录。
   * ref 是同步写、同步读的，第二次点击进来时它已经是 true。
   * 按钮 disabled 与遮罩只是**给人看的**，不作为并发保护。
   */
  const sending = useRef(false);
  const alert = useAlert();

  /* ── 案件要素（AI 预填 → 确认页核对）── 与手机端 `pages/Submit.tsx` 同一套装配。
     值与来源装在同一个 state 里，理由见那边的注释（分开放就一定会读到半旧半新）。 */
  const [ele, setEle] = useState<{ v: CaseElements; o: ElementOrigins }>({
    v: EMPTY_ELEMENTS,
    o: BLANK_ORIGINS,
  });
  const prefill = usePrefill((r) => {
    setEle((cur) => {
      const next = applyPrefill(cur.v, cur.o, r);
      return { v: next.elements, o: next.origins };
    });
  });
  const areas = useAreas(prefill.calls > 0 || prefill.status !== null);

  /* ★ 2026-09-04 批 ⑭：上屏的「合计」改用**原文**体积。
     `usedBudget()` 是 base64 长度（判断用），比原文胖 1/3 ——
     单件放到 40MB 之后，拿它上屏会显示「1 件 · 合计 53.4MB」。 */
  const used = usedRawBytes(files);
  const draft: ComplaintDraft = {
    name,
    phone,
    role,
    roleOther,
    targetPhone,
    person,
    org,
    text,
    files,
    elements: ele.v,
  };

  /**
   * 点身份 chip。**取消选中或换选别的项时，「其他」那个补充框的值当场清掉**
   * （2026-09-03 协会令 ⑩）——与手机端 `pages/Submit.tsx` 的 `pickRole` 逐行同构：
   * 两端交互必须一致，各写一套就会出现「手机上清了、PC 上没清」。
   */
  const pickRole = (r: string) => {
    const next = role === r ? '' : r;
    setRole(next);
    if (next !== ROLE_OTHER) setRoleOther('');
  };

  /** 「案件要素」那一块要的全部东西。装成一件，原样交给确认面板。 */
  const elementsPanel: CaseElementsPanel = {
    value: ele.v,
    origins: ele.o,
    onChange: (v, o) => setEle({ v, o }),
    running: prefill.phase === 'running',
    status: prefill.status,
    areas: areas.items,
    areasLoading: areas.loading,
    areasError: areas.error,
    onReloadAreas: areas.reload,
    disabled: busy,
  };

  const take = async (list: readonly File[]) => {
    if (list.length === 0) return;
    const { accepted, rejected } = await acceptEvidence(files, list);
    if (accepted.length) setFiles((v) => [...v, ...accepted]);
    // 没加进来的**逐条说清楚为什么**，不是笼统一句「上传失败」
    if (rejected.length) {
      alert({
        title: accepted.length ? '有文件没能加进来' : '这些文件没能加进来',
        text: rejected.join('\n'),
      });
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (busy) return;
    void take(Array.from(e.dataTransfer.files ?? []));
  };

  /**
   * 表单上的「提交投诉」＝**去确认屏**，不落库。
   * 校验仍在这一步跑完：不合格当场弹窗拦住，
   * **不放他确认完了才被告知「姓名不能为空」**。
   */
  const toConfirm = () => {
    if (sending.current) return;
    const bad = checkComplaint(draft);
    if (bad) {
      alert({ title: '还差一点', text: bad });
      return;
    }
    /* 补一脚，与手机端同一个理由：正常路径下文本框失焦时早就发过车了，
       这里因为「原文没变就不重算」连 fetch 都不会发；它兜的是没触发过失焦的路径。
       **不是**把发车点挪到这里 —— 挪过来就等于让他在这一步干等两秒。 */
    prefill.start(text);
    setStep('confirm');
    // 确认屏比表单短得多，留在原滚动位置会开在半空中
    window.scrollTo({ top: 0 });
  };

  /** 确认屏的「返回修改」。只换画法，state 原封不动。 */
  const backToEdit = () => {
    if (sending.current) return;
    setStep('form');
    window.scrollTo({ top: 0 });
  };

  /** 确认屏的「确认无误，提交投诉」＝真正落库。 */
  const doSubmit = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    try {
      const result = await submitComplaint(buildSubmitBody(draft));
      // 成功后不解锁 sending：本组件紧接着就被结果面板换掉了
      onDone(result);
    } catch (e) {
      // 写操作绝不静默回落：后端拒绝就把中文原因原样弹出来，并把闸门打开让他重试
      sending.current = false;
      setBusy(false);
      alert({ title: isInconclusive(e) ? WRITE_INCONCLUSIVE_TITLE : '提交失败', text: errText(e) });
    }
  };

  if (step === 'confirm') {
    return (
      <>
        <PcConfirm
          draft={draft}
          busy={busy}
          onBack={backToEdit}
          onConfirm={() => void doSubmit()}
          elements={elementsPanel}
        />
        {busy ? (
          <div className="pcovl" role="alert" aria-live="assertive">
            <div className="pcovl-box">
              <span className="pcspin" />
              <p>{WRITE_BUSY_TITLE}</p>
              <small>{WRITE_BUSY_HINT}</small>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <div className="pchero">
        <h1>在线投诉</h1>
        <p>
          请按自己的话把事情说清楚，并上传能说明问题的材料。带
          <span className="pcreq-inline">*</span>
          的为必填项。
        </p>
      </div>

      <form
        className="pcform"
        onSubmit={(e) => {
          e.preventDefault();
          toConfirm();
        }}
        noValidate
      >
        {/* 五块内容装在同一张纸上，用发丝线分节 —— 不切成五张白卡 */}
        <div className="pcsheet">
        {/* ── 一、投诉内容及诉求 ─────────────────────────────────────────── */}
        <section className="pcsec">
          <div className="pcsec-h">
            <h2>
              投诉内容及诉求<span className="pcreq">*</span>
            </h2>
          </div>
          <div className="pcfield">
            <textarea
              id="pc-text"
              className="pcinput pctext"
              rows={9}
              value={text}
              maxLength={5000}
              onChange={(e) => setText(e.target.value)}
              /* ★★ 提前发车（与手机端同一个落点）：鼠标一点到别处，原文就上路去算五项要素。
                 等他把剩下几栏填完再点提交，后端中位 1.62 秒的活早干完了。
                 焦点来回进出不会重复打接口 —— `usePrefill` 记着上一次算过的原文。 */
              onBlur={(e) => prefill.start(e.target.value)}
              placeholder="按您自己的话说清楚就行：什么时候、在哪个楼盘、哪家公司的谁、发生了什么、您希望怎么解决"
            />
            <div className="pccount">{text.length} 字</div>
          </div>
          {example !== null && (
            <div className="pcdemo">
              <span className="pcdemo-h">示范</span>
              <p style={{ whiteSpace: 'pre-line' }}>{example}</p>
            </div>
          )}
        </section>

        {/* ── 二、证据材料（必填，PC 支持拖拽） ─────────────────────── */}
        <section className="pcsec">
          <div className="pcsec-h">
            <h2>
              证据材料<span className="pcreq">*</span>
            </h2>
            {/* 一张都没选时不报「已选 0B／28MB」——那两个数字对空表单毫无意义，
                总量上限在下面的说明里已经写清楚了 */}
            {/* ★ 2026-09-04 批 ⑭：量词由「张」改「件」——放开录音与视频之后，
                「3 张」在一段录音加两个视频面前就是错的字。 */}
            <span className="pcsec-side">
              {files.length}/{MAX_FILES} 件{files.length ? ` · 合计 ${fmtSize(used)}` : ''}
            </span>
          </div>

          <div
            className={dragging ? 'pcdz pcdz--on' : 'pcdz'}
            onDragEnter={(e) => {
              e.preventDefault();
              dragDepth.current += 1;
              if (!busy) setDragging(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              dragDepth.current -= 1;
              if (dragDepth.current <= 0) {
                dragDepth.current = 0;
                setDragging(false);
              }
            }}
            onDrop={onDrop}
          >
            <span className="pcdz-ic">
              <UploadIcon />
            </span>
            <p className="pcdz-t">把材料拖到这里，或</p>
            <button
              type="button"
              className="pcbtn pcbtn-ghost"
              onClick={() => fileRef.current?.click()}
              disabled={files.length >= MAX_FILES}
            >
              {files.length >= MAX_FILES ? `已选满 ${MAX_FILES} 件` : '选择文件'}
            </button>
            {/* ★ 2026-09-04 批 ⑭：限额与「能传什么」一律取自真本，页面不写死数字。 */}
            <p className="pcdz-hint">
              合同、聊天记录、收款凭证、现场照片、通话录音、短视频等。{attachmentLimitHintText}
            </p>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept={evidenceAcceptAttr}
            multiple
            hidden
            onChange={(e) => void take(Array.from(e.target.files ?? []))}
          />

          {files.length ? (
            <ul className="pcshots">
              {files.map((f, i) => (
                <li className="pcshot" key={`${f.name}-${i}`}>
                  {/* ★ 2026-09-04 批 ⑭：非图片没有缩略图，画类别标记（见 Submit.tsx 同处注释） */}
                  {f.preview ? (
                    <img src={f.preview} alt={f.name} />
                  ) : (
                    <span className="pcshot-kind">{EVIDENCE_KIND_LABEL[f.kind]}</span>
                  )}
                  <button
                    type="button"
                    className="pcshot-rm"
                    aria-label={`移除 ${f.name}`}
                    title="移除"
                    onClick={() => setFiles((v) => v.filter((_, k) => k !== i))}
                  >
                    ×
                  </button>
                  <span className="pcshot-n" title={f.name}>
                    {f.name}
                  </span>
                  <span className="pcshot-s">{fmtSize(f.bytes)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>

        {/* ── 三、联系方式 ─────────────────────────────────────────── */}
        <section className="pcsec">
          <div className="pcsec-h">
            <h2>
              您的联系方式<span className="pcreq">*</span>
            </h2>
          </div>
          <div className="pcgrid2">
            <div className="pcfield">
              <label className="pclabel" htmlFor="pc-name">
                <PersonIcon />
                投诉人姓名
              </label>
              <input
                id="pc-name"
                className="pcinput"
                type="text"
                maxLength={MAX_NAME_LEN}
                value={name}
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
                placeholder="请输入真实姓名"
              />
            </div>
            <div className="pcfield">
              <label className="pclabel" htmlFor="pc-phone">
                <PhoneIcon />
                投诉人电话
              </label>
              <input
                id="pc-phone"
                className="pcinput"
                type="tel"
                inputMode="numeric"
                maxLength={11}
                value={phone}
                autoComplete="tel"
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                placeholder="请输入 11 位手机号"
              />
            </div>
          </div>
        </section>

        {/* ── 四、投诉人身份 ───────────────────────────────────────── */}
        <section className="pcsec">
          <div className="pcsec-h">
            <h2>投诉人身份</h2>
            <span className="pcsec-side">选填 · 单选</span>
          </div>
          <div className="pcchips" role="group" aria-label="投诉人身份">
            {COMPLAINT_ROLES.map((r) => (
              <button
                key={r}
                type="button"
                className={role === r ? 'pcchip pcchip--on' : 'pcchip'}
                aria-pressed={role === r}
                onClick={() => pickRole(r)}
              >
                {r}
              </button>
            ))}
          </div>
          {/* ★ 2026-09-03 协会令 ⑩：选中「其他」才展开这一行补充输入，未选中**不渲染**
              （不是 display:none）。取消选中／换选别的项时值由 `pickRole` 当场清空。
              占位、上限、提交形状与手机端**同一份**（`util/complaint-rules.ts`），
              身份本来就是选填，这里同样**不加任何校验**。 */}
          {role === ROLE_OTHER ? (
            <div className="pcfield pcfield--half">
              <input
                id="pc-role-other"
                className="pcinput"
                type="text"
                maxLength={MAX_ROLE_OTHER_LEN}
                value={roleOther}
                onChange={(e) => setRoleOther(e.target.value)}
                placeholder="请填写您的身份"
                aria-label="其他身份"
              />
            </div>
          ) : null}
        </section>

        {/* ── 五、被投诉方（人员线 ＋ 机构线，两条各自独立）────────────────
            选择器与手机端是**同一个组件**（components/RespondentPicker.tsx）：
            查询口径、截断提示、手工兜底、「手填不带编号」四件事只有一份实现，两端不会分叉。
            标「至少一项」而不是「必填」：填一块就成立，写「必填」会被理解成两块都得填。 */}
        <section className="pcsec">
          <div className="pcsec-h">
            <h2>被投诉方</h2>
            <span className="pcsec-side pcsec-req">至少填一项</span>
          </div>
          <div className="pcfield pcfield--half">
            <RespondentPicker person={person} org={org} onPerson={setPerson} onOrg={setOrg} />
          </div>
        </section>

        {/* ── 六、被投诉方联系方式 ───────────────────────────────────── */}
        <section className="pcsec">
          <div className="pcsec-h">
            <h2>被投诉方联系方式</h2>
            <span className="pcsec-side pcsec-req">必填</span>
          </div>
          <div className="pcfield pcfield--half">
            <input
              id="pc-target"
              className="pcinput"
              type="tel"
              inputMode="numeric"
              maxLength={11}
              value={targetPhone}
              onChange={(e) => setTargetPhone(e.target.value.replace(/\D/g, ''))}
              placeholder="填写被投诉方的 11 位联系电话"
            />
          </div>
        </section>
        </div>

        <div className="pcsubmit">
          {/* 这一钮**不落库**，只带去确认屏。所以不写「提交中…」——
              表单这一屏永远不会处在提交中，摆一个到不了的状态就是假状态。 */}
          <button type="submit" className="pcbtn pcbtn-main">
            提交投诉
          </button>
          {/* ★ 2026-09-03：来电主体「工作人员」→「成都房经协」，与两端提交成功页逐字同源
              （协会清单点的是成功页那两处，这一句是同一句话的第三处；不改就会出现
                「表单上说工作人员、成功页说成都房经协」）。 */}
          <p className="pcsubmit-say">请确认您留的电话准确，成都房经协在核实后会与您电话联系。</p>
        </div>
      </form>
    </>
  );
}
